import React, { useEffect, useMemo, useState } from "react";
import {
  Plus, Copy, Check, Trash2, Users, Link2, Pencil, Loader2, X, Landmark,
} from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { slugify } from "../mock";
import { getMasters, createMaster, updateMaster, deleteMaster } from "../api";
import { toast } from "../hooks/use-toast";

const emptyForm = { name: "", email: "", phone: "", code: "", bankLink: "" };

export default function MastersPanel({ onChanged }) {
  const [masters, setMasters] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const baseUrl = window.location.origin;

  const refresh = async () => {
    try { setMasters(await getMasters()); } catch { /* ignore */ }
  };
  useEffect(() => { refresh(); }, []);

  const linkFor = (code) => `${baseUrl}/api/convite/${code}`;

  const openNew = () => { setEditing(null); setForm(emptyForm); setDialog(true); };
  const openEdit = (m) => { setEditing(m); setForm({ name: m.name, email: m.email || "", phone: m.phone || "", code: m.code, bankLink: m.bankLink || "" }); setDialog(true); };

  const save = async () => {
    if (!form.name.trim()) {
      toast({ title: "Informe o nome do master", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      if (editing) await updateMaster(editing.id, form);
      else await createMaster(form);
      setDialog(false);
      await refresh();
      onChanged?.();
      toast({ title: editing ? "Master atualizado" : "Master criado", description: "Alterações salvas com sucesso." });
    } catch {
      toast({ title: "Erro ao salvar master", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const copyLink = (code) => {
    navigator.clipboard?.writeText(linkFor(code));
    setCopied(code);
    setTimeout(() => setCopied(null), 1800);
    toast({ title: "Link copiado!", description: "Envie este link para os parceiros do master." });
  };

  const confirmDelete = async () => {
    await deleteMaster(toDelete.id);
    setToDelete(null);
    await refresh();
    onChanged?.();
    toast({ title: "Master removido" });
  };

  const list = useMemo(() => masters, [masters]);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Cada master possui um <strong>link exclusivo</strong>. Parceiros cadastrados por esse link ficam vinculados ao master.
        </p>
        <Button onClick={openNew} className="bg-[#465EFF] text-white hover:bg-[#3448d6]">
          <Plus className="mr-2 h-4 w-4" /> Novo Master
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {list.length === 0 && (
          <p className="col-span-full py-10 text-center text-slate-400">Nenhum master cadastrado ainda.</p>
        )}
        {list.map((m) => (
          <div key={m.id} className="flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#FCFC30] font-heading text-lg font-extrabold text-[#0A1B6B]">
                  {(m.name?.[0] || "M").toUpperCase()}
                </div>
                <div>
                  <div className="font-semibold text-[#0A1B6B]">{m.name}</div>
                  <div className="font-mono text-xs text-[#465EFF]">{m.code}</div>
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(m)} className="h-8 w-8 text-slate-400 hover:text-[#465EFF]">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => setToDelete(m)} className="h-8 w-8 text-slate-400 hover:text-red-500">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
              <Users className="h-4 w-4 text-[#465EFF]" />
              {m.partnerCount ?? 0} parceiro(s)
            </div>

            <div className="mt-3 flex items-center gap-2 rounded-lg bg-slate-50 p-2">
              <Link2 className="h-4 w-4 shrink-0 text-slate-400" />
              <span data-testid={`master-invite-link-${m.code}`} className="flex-1 truncate text-xs text-slate-500">{linkFor(m.code)}</span>
              <Button
                data-testid={`master-copy-link-${m.code}`}
                size="sm"
                variant="outline"
                onClick={() => copyLink(m.code)}
                className="h-7 shrink-0 border-slate-200 px-2 text-xs text-[#0A1B6B]"
              >
                {copied === m.code ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
              </Button>
            </div>
            <div data-testid={`master-bank-status-${m.code}`} className={`mt-2 flex items-center gap-1.5 text-xs ${m.bankLink ? "text-green-600" : "text-amber-600"}`}>
              <Landmark className="h-3.5 w-3.5" />
              {m.bankLink ? "Link do banco cadastrado" : "Sem link do banco — edite para adicionar"}
            </div>
            <p className="mt-1.5 text-[11px] text-slate-400">Compartilhe este link: a prévia do WhatsApp mostra "Convite de {m.name}".</p>
          </div>
        ))}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#0A1B6B]">
              {editing ? "Editar Master" : "Novo Master"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-medium text-slate-700">Nome do Master *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ex: André Souza" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-medium text-slate-700">Nome no link (opcional)</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
                placeholder="Ex: andre (deixe vazio para gerar automaticamente)"
              />
              <p className="truncate text-xs text-slate-400">
                Link: <span className="font-mono text-[#465EFF]">{baseUrl}/api/convite/{slugify(form.code || form.name) || "..."}</span>
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-medium text-slate-700">Link do banco (substabelecimento)</Label>
              <Input
                data-testid="master-bank-link-input"
                value={form.bankLink}
                onChange={(e) => setForm((f) => ({ ...f, bankLink: e.target.value }))}
                placeholder="https://cadastro.consorcios.digital/?p=..."
              />
              <p className="text-xs text-slate-400">Usado pelo botão "Preencher no banco" dos parceiros deste master.</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-medium text-slate-700">E-mail</Label>
              <Input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="master@email.com" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label className="text-sm font-medium text-slate-700">Telefone</Label>
              <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="55 (11) 90000-0000" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}><X className="mr-2 h-4 w-4" /> Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#465EFF] text-white hover:bg-[#3448d6]">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir master?</AlertDialogTitle>
            <AlertDialogDescription>
              O master {toDelete?.name} será removido. Os parceiros já cadastrados por ele continuarão existindo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
